import React, { useState, useEffect, useCallback } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { obtenerProveedores, registrarCompra } from '../../services/comprasServicio';
import { obtenerInsumos } from '../../services/insumosServicio';

const hoyISO = () => new Date().toISOString().slice(0, 10);

const RecepcionCompras = () => {
    const [proveedores, setProveedores] = useState([]);
    const [productos, setProductos] = useState([]);
    const [proveedorId, setProveedorId] = useState('');
    const [numeroDocumento, setNumeroDocumento] = useState('');
    const [tipoDocumento, setTipoDocumento] = useState('FACTURA');
    const [fecha, setFecha] = useState(hoyISO());
    const [nota, setNota] = useState('');
    const [busquedaProducto, setBusquedaProducto] = useState('');
    const [carrito, setCarrito] = useState([]);
    const [enviando, setEnviando] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    const cargarDatos = useCallback(async () => {
        setIsLoading(true);
        try {
            const [provs, insumos] = await Promise.all([obtenerProveedores(), obtenerInsumos()]);
            setProveedores(provs || []);
            setProductos(insumos || []);
        } catch (error) {
            toast.error("Error al cargar proveedores/insumos");
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => { cargarDatos(); }, [cargarDatos]);

    const agregarAlCarrito = (insumo) => {
        if (carrito.find(item => item.insumo_id === insumo.id)) {
            toast.info("El insumo ya está en la lista", { autoClose: 1500 });
            return;
        }
        setCarrito([...carrito, {
            insumo_id: insumo.id,
            nombre: insumo.nombre,
            cantidad: 1,
            costo_unitario: insumo.costo || 0
        }]);
        setBusquedaProducto('');
    };

    const actualizarLinea = (id, campo, valor) => {
        setCarrito(carrito.map(item => item.insumo_id === id ? { ...item, [campo]: valor } : item));
    };

    const eliminarLinea = (id) => {
        setCarrito(carrito.filter(item => item.insumo_id !== id));
    };

    const total = carrito.reduce((acc, item) => acc + (parseFloat(item.cantidad || 0) * parseFloat(item.costo_unitario || 0)), 0);

    const productosFiltrados = productos.filter(p =>
        busquedaProducto.length > 0 &&
        (p.nombre || '').toLowerCase().includes(busquedaProducto.toLowerCase()) &&
        !carrito.find(item => item.insumo_id === p.id)
    ).slice(0, 8);

    const handleRegistrar = async () => {
        if (!proveedorId) return toast.warning("Selecciona un proveedor");
        if (!fecha) return toast.warning("Indica la fecha de la recepción");
        if (carrito.length === 0) return toast.warning("Agrega al menos un insumo/producto");

        setEnviando(true);
        try {
            const respuesta = await registrarCompra({
                proveedor_id: Number(proveedorId),
                numero_documento: numeroDocumento,
                tipo_documento: tipoDocumento,
                fecha,
                nota,
                items: carrito.map(item => ({
                    insumo_id: item.insumo_id,
                    cantidad: parseFloat(item.cantidad || 0),
                    costo_unitario: parseInt(item.costo_unitario || 0, 10)
                }))
            });
            if (respuesta && respuesta.items_omitidos && respuesta.items_omitidos.length > 0) {
                toast.warning(`Recepción guardada, pero ${respuesta.items_omitidos.length} línea(s) no se pudieron procesar.`, { autoClose: 5000 });
            } else {
                toast.success("Recepción registrada. Stock actualizado.");
            }
            setCarrito([]);
            setNumeroDocumento('');
            setNota('');
        } catch (error) {
            const msg = (error.response && error.response.data && error.response.data.error) || "Error al registrar la recepción";
            toast.error(msg);
        } finally {
            setEnviando(false);
        }
    };

    return (
        <div className="bg-light min-vh-100 py-5 px-3">
            <ToastContainer position="bottom-center" autoClose={2500} hideProgressBar />

            <div className="container" style={{ maxWidth: '1100px' }}>
                <div className="mb-5">
                    <h1 className="h2 font-weight-bold text-dark mb-1">Recepción de Compras de Insumos</h1>
                    <p className="text-muted">Registra el ingreso de insumos comprados a un proveedor, con documento y trazabilidad.</p>
                </div>

                {isLoading ? (
                    <div className="text-center py-5 text-muted">Cargando proveedores e insumos...</div>
                ) : (
                <div className="row">
                    <div className="col-md-5 mb-4">
                        <div className="card border-0 shadow-sm rounded-xl p-4">
                            <h6 className="text-uppercase small font-weight-bold text-muted mb-3">Datos de la recepción</h6>

                            <div className="form-group mb-3">
                                <label className="small font-weight-bold text-muted">Proveedor</label>
                                <select className="form-control form-control-lg bg-light border-light" value={proveedorId} onChange={(e) => setProveedorId(e.target.value)}>
                                    <option value="">Selecciona un proveedor...</option>
                                    {proveedores.map(p => <option key={p.id} value={p.id}>{p.nombre}</option>)}
                                </select>
                                {proveedores.length === 0 && (
                                    <small className="text-muted">No tienes proveedores creados. Ve a la sección Proveedores para agregar uno.</small>
                                )}
                            </div>

                            <div className="row">
                                <div className="col-6 form-group mb-3">
                                    <label className="small font-weight-bold text-muted">Tipo Documento</label>
                                    <select className="form-control bg-light border-light" value={tipoDocumento} onChange={(e) => setTipoDocumento(e.target.value)}>
                                        <option value="FACTURA">Factura</option>
                                        <option value="BOLETA">Boleta</option>
                                        <option value="GUIA">Guía de despacho</option>
                                        <option value="OTRO">Otro</option>
                                    </select>
                                </div>
                                <div className="col-6 form-group mb-3">
                                    <label className="small font-weight-bold text-muted">N° Documento</label>
                                    <input type="text" className="form-control bg-light border-light" value={numeroDocumento} onChange={(e) => setNumeroDocumento(e.target.value)} />
                                </div>
                            </div>

                            <div className="form-group mb-3">
                                <label className="small font-weight-bold text-muted">Fecha</label>
                                <input type="date" className="form-control bg-light border-light" value={fecha} onChange={(e) => setFecha(e.target.value)} />
                            </div>

                            <div className="form-group mb-0">
                                <label className="small font-weight-bold text-muted">Nota</label>
                                <textarea className="form-control bg-light border-light" rows="2" value={nota} onChange={(e) => setNota(e.target.value)} placeholder="Observaciones..." />
                            </div>
                        </div>
                    </div>

                    <div className="col-md-7">
                        <div className="card border-0 shadow-sm rounded-xl p-4 mb-3">
                            <h6 className="text-uppercase small font-weight-bold text-muted mb-3">Agregar insumo</h6>
                            <div className="position-relative">
                                <input
                                    type="text" className="form-control form-control-lg bg-light border-light"
                                    placeholder="Buscar por nombre..."
                                    value={busquedaProducto}
                                    onChange={(e) => setBusquedaProducto(e.target.value)}
                                />
                                {productosFiltrados.length > 0 && (
                                    <div className="list-group shadow-sm position-absolute w-100" style={{ zIndex: 20, maxHeight: '220px', overflowY: 'auto' }}>
                                        {productosFiltrados.map(p => (
                                            <button type="button" key={p.id} className="list-group-item list-group-item-action" onClick={() => agregarAlCarrito(p)}>
                                                {p.nombre}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="card border-0 shadow-sm rounded-xl overflow-hidden">
                            <div className="table-responsive">
                                <table className="table align-middle mb-0">
                                    <thead className="bg-light">
                                        <tr>
                                            <th className="border-0 px-4 py-3 text-muted small text-uppercase">Insumo</th>
                                            <th className="border-0 py-3 text-muted small text-uppercase" style={{ width: '110px' }}>Cantidad</th>
                                            <th className="border-0 py-3 text-muted small text-uppercase" style={{ width: '140px' }}>Costo Unit.</th>
                                            <th className="border-0 py-3 text-muted small text-uppercase text-right" style={{ width: '120px' }}>Subtotal</th>
                                            <th className="border-0" style={{ width: '50px' }}></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {carrito.length === 0 ? (
                                            <tr><td colSpan="5" className="text-center py-5 text-muted">Agrega insumos para la recepción.</td></tr>
                                        ) : (
                                            carrito.map(item => (
                                                <tr key={item.insumo_id}>
                                                    <td className="px-4 py-2 font-weight-bold text-dark">{item.nombre}</td>
                                                    <td className="py-2">
                                                        <input
                                                            type="number" min="0" step="0.001" className="form-control form-control-sm"
                                                            value={item.cantidad}
                                                            onChange={(e) => actualizarLinea(item.insumo_id, 'cantidad', e.target.value)}
                                                        />
                                                    </td>
                                                    <td className="py-2">
                                                        <input
                                                            type="number" min="0" step="1" className="form-control form-control-sm"
                                                            value={item.costo_unitario}
                                                            onChange={(e) => actualizarLinea(item.insumo_id, 'costo_unitario', e.target.value)}
                                                        />
                                                    </td>
                                                    <td className="py-2 text-right font-weight-bold">
                                                        {new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', minimumFractionDigits: 0 }).format((item.cantidad || 0) * (item.costo_unitario || 0))}
                                                    </td>
                                                    <td className="py-2 text-center">
                                                        <button className="btn btn-sm btn-light text-danger" onClick={() => eliminarLinea(item.insumo_id)}>
                                                            <i className="fas fa-times"></i>
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                            <div className="d-flex justify-content-between align-items-center p-4 bg-light">
                                <div>
                                    <span className="text-muted small text-uppercase d-block">Total recepción</span>
                                    <span className="h4 font-weight-bold text-dark mb-0">
                                        {new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', minimumFractionDigits: 0 }).format(total)}
                                    </span>
                                </div>
                                <button className="btn btn-primary btn-lg rounded-pill px-5 shadow-sm" disabled={enviando} onClick={handleRegistrar}>
                                    {enviando ? 'Guardando...' : 'Registrar Recepción'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
                )}
            </div>

            <style jsx>{`
                .rounded-xl { border-radius: 1.25rem !important; }
            `}</style>
        </div>
    );
};

export default RecepcionCompras;
